import { gsap } from 'gsap'
import { Observer } from 'gsap/Observer'
import { type RefObject, useCallback, useEffect, useRef } from 'react'

gsap.registerPlugin(Observer)

// Pages 0..VIRTUAL_PAGE_COUNT-1: content swap in-frame (container stays at y=0)
// Pages VIRTUAL_PAGE_COUNT..TOTAL_PAGE_COUNT-1: container animates vertically
const VIRTUAL_PAGE_COUNT = 2

function containerYForPage(index: number): number {
  if (index < VIRTUAL_PAGE_COUNT) return 0
  return (index - VIRTUAL_PAGE_COUNT + 1) * -window.innerHeight
}

// How long wheel events must be quiet before unlocking (handles Mac trackpad momentum)
const SETTLE_DELAY_MS = 200
// Safety cap — always unlock after this long regardless
const MAX_LOCK_MS = 1500

export function useScrollTrigger(
  containerRef: RefObject<HTMLElement | null>,
  totalPages = 1,
  callbacks?: { onPageChange?: (pageIndex: number) => void }
) {
  const currentPage = useRef(0)
  const isAnimating = useRef(false)
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const safetyTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Stable ref so Observer closure always uses latest callbacks without re-creating
  const callbacksRef = useRef(callbacks)
  useEffect(() => {
    callbacksRef.current = callbacks
  })

  // Unlock only once wheel events have gone quiet for SETTLE_DELAY_MS.
  // Each incoming wheel event while locked resets the settle timer, so
  // Mac trackpad momentum (many small decaying events) can't sneak through.
  const lockUntilSettled = useCallback((minLockMs: number) => {
    isAnimating.current = true

    const clearTimers = () => {
      if (settleTimer.current) clearTimeout(settleTimer.current)
      if (safetyTimer.current) clearTimeout(safetyTimer.current)
    }

    const unlock = () => {
      clearTimers()
      isAnimating.current = false
      window.removeEventListener('wheel', onWheel)
    }

    // Reset settle countdown on every wheel event
    const onWheel = () => {
      if (settleTimer.current) clearTimeout(settleTimer.current)
      settleTimer.current = setTimeout(unlock, SETTLE_DELAY_MS)
    }

    // Start the settle countdown after the minimum lock period
    setTimeout(() => {
      window.addEventListener('wheel', onWheel, { passive: true })
      settleTimer.current = setTimeout(unlock, SETTLE_DELAY_MS)
      safetyTimer.current = setTimeout(unlock, MAX_LOCK_MS)
    }, minLockMs)

    return clearTimers
  }, [])

  const goToPage = useCallback(
    (index: number) => {
      const container = containerRef.current
      if (!container) return
      if (isAnimating.current || index === currentPage.current) return
      if (index < 0 || index >= totalPages) return

      const prev = currentPage.current
      currentPage.current = index

      const prevY = containerYForPage(prev)
      const nextY = containerYForPage(index)

      if (prevY === nextY) {
        // Virtual content swap — lock for CSS transition then wait for momentum to settle
        callbacksRef.current?.onPageChange?.(index)
        lockUntilSettled(600)
      } else {
        isAnimating.current = true
        gsap.to(container, {
          y: nextY,
          duration: 0.8,
          ease: 'power2.inOut',
          onStart: () => callbacksRef.current?.onPageChange?.(index),
          onComplete: () => { lockUntilSettled(0) },
        })
      }
    },
    [containerRef, lockUntilSettled, totalPages]
  )

  useEffect(() => {
    const obs = Observer.create({
      type: 'wheel,touch,pointer',
      wheelSpeed: 1,
      onDown: () => goToPage(currentPage.current + 1),
      onUp: () => goToPage(currentPage.current - 1),
      tolerance: 50,
      preventDefault: true,
    })

    const NEXT_KEYS = new Set(['ArrowDown', 'ArrowRight', 'PageDown', ' '])
    const PREV_KEYS = new Set(['ArrowUp', 'ArrowLeft', 'PageUp'])

    const onKeyDown = (e: KeyboardEvent) => {
      // Don't hijack keyboard shortcuts or input fields
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if (e.metaKey || e.ctrlKey || e.altKey) return

      if (NEXT_KEYS.has(e.key)) {
        e.preventDefault()
        goToPage(currentPage.current + 1)
      } else if (PREV_KEYS.has(e.key)) {
        e.preventDefault()
        goToPage(currentPage.current - 1)
      }
    }

    window.addEventListener('keydown', onKeyDown)

    return () => {
      obs.kill()
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [goToPage])

  return { goToPage }
}
